import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { OutlineButton } from '@/app/components/OutlineButton'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Typography } from '@/app/components/Typography'
import { MydraIcon } from '@/app/icons/MydraIcon'
import { useAuth } from '@/app/providers/Auth/Auth'
import { OAuthService, OAuthConsentRequest } from '@/app/services/oauth'

/**
 * Mydra on the left, whoever is asking on the right.
 *
 * The client's logo comes from what it declared at dynamic registration, so it
 * is untrusted and often absent — it falls back to a monogram, which is the
 * normal case rather than an error state. A failed image load falls back the
 * same way, so a dead URL never leaves a broken frame on the screen.
 */
function ConnectionHeader({
  clientName,
  logoUri,
}: {
  clientName: string
  logoUri: string | null
}) {
  const [logoFailed, setLogoFailed] = useState(false)
  const showLogo = logoUri && !logoFailed

  return (
    <div className="flex items-center justify-center gap-5">
      <div className="flex items-center justify-center w-20 h-20">
        {/* The mark is 32x30, so height drives the size and width follows —
            forcing both would squash it. */}
        <MydraIcon className="w-auto h-14" />
      </div>

      <div aria-hidden className="flex gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
      </div>

      <div className="flex items-center justify-center w-20 h-20">
        {showLogo ? (
          <img
            src={logoUri}
            alt=""
            referrerPolicy="no-referrer"
            className="object-contain w-16 h-16"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <span className="text-3xl font-semibold text-gray-400">
            {clientName.trim().charAt(0).toUpperCase() || '?'}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Where an MCP client's OAuth flow surfaces to the user.
 *
 * The API parks the authorization request and sends the browser here with a
 * single `request_id`. This page only has to say who is asking and what they
 * get, then hand the answer back — the API builds the redirect.
 *
 * It sits behind `ProtectedRoute`, so an unauthenticated visitor goes through
 * the normal login and returns to the same request.
 */
export function OAuthConsentPage() {
  const intl = useIntl()
  const { firstName, email } = useAuth()
  const [searchParams] = useSearchParams()

  const requestId = searchParams.get('request_id') ?? ''

  const [consentRequest, setConsentRequest] =
    useState<OAuthConsentRequest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!requestId) {
      setError(
        intl.formatMessage({
          id: 'OAuthConsent.missing-request',
          defaultMessage:
            'This link is incomplete. Start the connection again from the app you were using.',
        })
      )
      return
    }

    OAuthService.getConsentRequest(requestId)
      .then(setConsentRequest)
      .catch(() =>
        setError(
          intl.formatMessage({
            id: 'OAuthConsent.expired-request',
            defaultMessage:
              'This connection request expired. Start it again from the app you were using.',
          })
        )
      )
  }, [requestId, intl])

  async function resolve(approved: boolean) {
    setIsSubmitting(true)

    try {
      // A full navigation, not a router push: the destination belongs to the
      // client that started the flow, not to this app.
      window.location.href = await OAuthService.resolveConsent(
        requestId,
        approved
      )
    } catch {
      setIsSubmitting(false)
      setError(
        intl.formatMessage({
          id: 'OAuthConsent.resolve-failed',
          defaultMessage: 'Something went wrong. Start the connection again.',
        })
      )
    }
  }

  return (
    <>
      <Helmet>
        <title>Connect an app - Mydra</title>
      </Helmet>

      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="flex flex-col w-full max-w-md gap-6">

          {error && (
            <Typography variant="body1" className="text-center">
              {error}
            </Typography>
          )}

          {!error && !consentRequest && (
            <Typography variant="body1" className="text-center">
              {intl.formatMessage({
                id: 'OAuthConsent.loading',
                defaultMessage: 'Loading…',
              })}
            </Typography>
          )}

          {consentRequest && (
            <>
              <ConnectionHeader
                clientName={consentRequest.clientName}
                logoUri={consentRequest.logoUri}
              />

              <Typography variant="h5" className="text-center">
                {intl.formatMessage(
                  {
                    id: 'OAuthConsent.title',
                    defaultMessage: 'Connect {clientName} to Mydra?',
                  },
                  { clientName: consentRequest.clientName }
                )}
              </Typography>

              <div className="flex flex-col gap-3 p-4 rounded-lg bg-gray-50">
                <Typography variant="body2" className="font-semibold">
                  {intl.formatMessage({
                    id: 'OAuthConsent.grants',
                    defaultMessage: 'It will be able to read:',
                  })}
                </Typography>

                <ul className="pl-5 text-sm list-disc text-gray-700">
                  <li>
                    {intl.formatMessage({
                      id: 'OAuthConsent.grant-work-map',
                      defaultMessage: 'Your work map and its tasks',
                    })}
                  </li>
                  <li>
                    {intl.formatMessage({
                      id: 'OAuthConsent.grant-process-docs',
                      defaultMessage: 'Your process docs',
                    })}
                  </li>
                  <li>
                    {intl.formatMessage({
                      id: 'OAuthConsent.grant-agent-specs',
                      defaultMessage: 'Your agent specs',
                    })}
                  </li>
                </ul>

                <Typography variant="body2" className='font-semibold'>
                  {intl.formatMessage({
                    id: 'OAuthConsent.writes',
                    defaultMessage: 'And to record:',
                  })}
                </Typography>

                <ul className="pl-5 text-sm list-disc text-gray-700">
                  <li>
                    {intl.formatMessage({
                      id: 'OAuthConsent.write-agent-run',
                      defaultMessage:
                        'When you use one of your agent specs, so you can see which are still in use',
                    })}
                  </li>
                </ul>

                <Typography variant="body2" className="text-gray-600">
                  {intl.formatMessage({
                    id: 'OAuthConsent.nothing-else',
                    defaultMessage:
                      'It cannot change or delete anything else, and it cannot see your payments or documents.',
                  })}
                </Typography>
              </div>

              {/* Anyone can register a client under any name, and the logo is
                  matched from that name — so the destination host is the only
                  thing on this screen a stranger cannot dress up. */}
              <div className="flex flex-col gap-1 text-center">
                <Typography variant="body2" className="text-gray-600">
                  {intl.formatMessage(
                    {
                      id: 'OAuthConsent.sends-to',
                      defaultMessage: 'Your data will be sent to {host}',
                    },
                    {
                      host: (
                        <span className="font-semibold text-mydra-black">
                          {consentRequest.redirectOrigin}
                        </span>
                      ),
                    },
                  )}
                </Typography>

                <Typography variant="body2" className="text-gray-500">
                  {intl.formatMessage({
                    id: 'OAuthConsent.unverified',
                    defaultMessage:
                      'Mydra has not verified this application. Only continue if you started this yourself.',
                  })}
                </Typography>
              </div>

              <Typography variant="body2" className="text-center text-gray-600">
                {intl.formatMessage(
                  {
                    id: 'OAuthConsent.acting-as',
                    defaultMessage: 'You are signed in as {name}',
                  },
                  { name: firstName ? `${firstName} (${email})` : email }
                )}
              </Typography>

              <div className="flex gap-3">
                <OutlineButton
                  className="flex-1"
                  disabled={isSubmitting}
                  onClick={() => resolve(false)}
                >
                  {intl.formatMessage({
                    id: 'OAuthConsent.deny',
                    defaultMessage: 'Cancel',
                  })}
                </OutlineButton>

                <PrimaryButton
                  className="flex-1"
                  disabled={isSubmitting}
                  onClick={() => resolve(true)}
                >
                  {intl.formatMessage({
                    id: 'OAuthConsent.approve',
                    defaultMessage: 'Connect',
                  })}
                </PrimaryButton>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
